import { FC, useState } from "react";
import * as _ from "lodash";
import { useDeepCompareCallback, useDeepCompareMemo } from "use-deep-compare";

import { GAEvent } from "@ctra/analytics";
import { MetricEntity } from "@ctra/api";
import { useTranslation, Enterprise as Content } from "@ctra/i18n";
import { SearchOutlined, AntDesignSelect as Select, Analytics, Tag, Row, Col } from "@ctra/components";

import { useDataDictionary } from "@base";
import { BaseIntf, GACategories } from "@analytics";

import styles from "./MetricPicker.module.less";
import { isDebugMode } from "@ctra/utils";

const { OptGroup, Option } = Select;

const {
  V3: { CategoryIcon }
} = Analytics;

interface MetricPickerProps {
  /**
   * Disables the metric picker
   */
  disabled?: boolean;
  /**
   * Tell whether the option is disabled
   * @param {MetricEntity} metric
   * @returns {boolean}
   */
  isOptionDisabled?: (metric: MetricEntity) => boolean;
  /**
   * Callback function to handle the change of the selected metric
   * @param {string} metric
   */
  onChange: (metric: MetricEntity["id"]) => void;
  /**
   * Blacklist of metric IDs to be excluded from the options
   */
  blacklist?: Array<BaseIntf>;
  /**
   * The selected metric
   */
  value?: MetricEntity["id"];
}

const {
  layouts: { section },
  analytics: {
    config: {
      search: { placeholder },
      metric: { displayName },
      projections
    }
  }
} = Content;

/**
 * Metric picker
 * @param {string | undefined} value
 * @param {boolean | undefined} disabled
 * @param {(metric: MetricEntity["id"]) => void} onChange
 * @param {((metric: MetricEntity) => boolean) | undefined} isOptionDisabled
 * @param {Array<BaseIntf> | undefined} blacklist
 * @returns {JSX.Element}
 * @constructor
 */
export const MetricPicker: FC<MetricPickerProps> = ({
  value,
  disabled,
  onChange,
  isOptionDisabled,
  blacklist
}) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>("");

  const {
    metrics,
    groups: { byCategory, families, projectionMetrics }
  } = useDataDictionary();

  /**
   * Get all metrics by group
   * @type {{[p: string]: Dictionary<MetricEntity>}}
   */
  const metricsByGroup: Record<string, Record<MetricEntity["id"], MetricEntity>> = useDeepCompareMemo(
    () => _.mapValues(byCategory, (metrics) => _.keyBy(metrics, "id")),
    [byCategory]
  );

  /**
   * Get rid of the blacklisted metrics and deduplicate the metrics in the same family if they fall within the same group
   * @type {() => Dictionary<any>}
   */
  const filterBlacklisted = useDeepCompareCallback(() => {
    const excludedVariants = _.map(blacklist, "variantID");

    const withoutBlacklisted = _.size(excludedVariants)
      ? _.omitBy(
          _.mapValues(metricsByGroup, (group) =>
            _.omitBy(group, ({ variants }) => _.size(_.without(variants, ...excludedVariants)) === 0)
          ),
          _.isEmpty
        )
      : metricsByGroup;

    return _.mapValues(withoutBlacklisted, (group) => {
      /**
       * Group the metrics by family and keep the ones that have more than one metric
       * @type {Dictionary<[unknown, ...unknown[]]>}
       */
      const familiesToDeduplicate = _.omitBy(
        _.groupBy(group, "family"),
        (group, familyName) => _.size(group) === 1 || familyName === "undefined"
      );

      /**
       * Make a list of the metrics to be dropped for deduplication
       * @type {MetricEntity["id"][]}
       */
      const metricsToDrop = _.flatMap(familiesToDeduplicate, (group) => {
        /**
         * Filter out the selected metric
         * @type {unknown[]}
         */
        const withoutSelected = _.reject(group, ["id", value]);

        if (_.size(withoutSelected) === _.size(group)) {
          /**
           * Find the metric with the lowest family index
           */
          const lowestIndex = _.minBy(withoutSelected, "familyIndex");

          /**
           * Drop everything but the one with the lowest family index
           * @type {unknown[]}
           */
          const metricList = _.reject(withoutSelected, ["id", lowestIndex?.id]);

          return _.map(metricList, "id");
        }

        return _.map(withoutSelected, "id");
      });

      return _.omit(group, metricsToDrop);
    });
  }, [value, metricsByGroup, blacklist]);

  /**
   * Get the dropdown data
   * @type {{key: number, label: any, options: {keywords: any, key: any, value: any, label: any}[]}[]}
   */
  const items = useDeepCompareMemo(() => {
    const metricGroups = filterBlacklisted();

    return _.map(metricGroups, (group, groupName) => {
      const label = t<string>(section.title(groupName), { makeDefaultValue: true });

      /**
       * Unordered list of options
       * @type {{keywords: Array<string> | undefined, key: string, value: string, label: any}[]}
       */
      const options = _.map(group, (metric, id) => {
        const { keywords, name, family } = metric;

        return {
          keywords,
          key: id,
          value: id,
          disabled: _.isFunction(isOptionDisabled) ? isOptionDisabled(metric) : false,
          label: t(displayName(_.defaultTo(family, name)), {
            makeDefaultValue: true
          })
        };
      });

      return {
        label,
        key: groupName,
        options: _.orderBy(options, ({ label }) => label.toLowerCase())
      };
    });
  }, [value, metricsByGroup, blacklist]);

  /**
   * Filter the options based on search query
   */
  const filteredItems = useDeepCompareMemo(
    () =>
      items.reduce((acc: typeof items, data) => {
        const filteredGroupOptions = data.options.filter(
          (option) =>
            option.label.toLowerCase().includes(filter.toLowerCase()) ||
            _.some(option.keywords, (keyword: string) => keyword.toLowerCase().includes(filter.toLowerCase()))
        );

        if (filteredGroupOptions.length > 0) {
          acc.push({
            ...data,
            options: filteredGroupOptions
          });
        }

        return acc;
      }, []),
    [items, filter]
  );

  return (
    <div className={styles.Space}>
      {value ? (
        <CategoryIcon
          category={_.get(families, [value, 0, "category"], _.get(metrics, [value, "category"]))}
          className={styles.Icon}
        />
      ) : (
        <SearchOutlined className={styles.Icon} />
      )}
      <Select
        disabled={disabled}
        size="large"
        style={{ width: "100%" }}
        placeholder={t(placeholder)}
        onSearch={setFilter}
        onChange={(value) => {
          GAEvent(
            GACategories.configurator,
            "Select metric",
            JSON.stringify({
              metric: _.get(metrics, [value, "name"], value)
            })
          );

          onChange(value);
        }}
        value={value}
        filterOption={false}
        showSearch
        showArrow={false}
      >
        {filteredItems.map(({ key: optGroupKey, label: optGroupLabel, options }) => (
          <OptGroup key={optGroupKey} label={optGroupLabel}>
            {options.map(({ label, value, disabled }) => (
              <Option disabled={disabled} key={value} keyPath={[optGroupKey, value]} value={value}>
                <Row justify="space-between">
                  {isDebugMode() ? `${label} (${value})` : label}
                  <Col>
                    {_.includes(projectionMetrics, value) && (
                      <Tag className={styles.Tag}>{t(projections)}</Tag>
                    )}
                  </Col>
                </Row>
              </Option>
            ))}
          </OptGroup>
        ))}
      </Select>
    </div>
  );
};
